/**
 * Financial-model derivation helpers (CHAP-300).
 *
 * `beds` + `rentPerBedMonthly` are first-class model drivers. When BOTH are set
 * (positive), the base-year revenue/NOI derives as `beds * rent * 12` and flows
 * into the 25-year projection exactly like the manual NOI slider did. When
 * either is unset (null/0), the legacy manual `noi` value is used unchanged.
 *
 * Unit note: `rentPerBedMonthly` is stored in the SAME money scale as `noi`, so
 * the product `beds * rent * 12` reproduces the stored NOI (to integer
 * rounding). This keeps displayed numbers stable when the derivation is first
 * enabled - see the omega-zeta seed derivation.
 */

export interface NoiDerivationInputs {
  beds: number | null | undefined;
  rentPerBedMonthly: number | null | undefined;
  noi: number | null | undefined;
}

/** True when beds + rent are both present and positive (derivation is active). */
export function hasBedsRent(m: NoiDerivationInputs): boolean {
  return (m.beds ?? 0) > 0 && (m.rentPerBedMonthly ?? 0) > 0;
}

/**
 * Base-year NOI used by the projection. `beds * rent * 12` when both are set,
 * otherwise the legacy manual `noi` (or 0 when null).
 */
export function deriveNoiBase(m: NoiDerivationInputs): number {
  if (hasBedsRent(m)) {
    return (m.beds as number) * (m.rentPerBedMonthly as number) * 12;
  }
  return m.noi ?? 0;
}

/**
 * Inverse used for seeding: the rent-per-bed implied by an existing NOI and a
 * bed count, so that `beds * impliedRent * 12` reproduces the NOI (to integer
 * rounding). Returns 0 when beds is non-positive.
 */
export function impliedRentPerBedMonthly(noi: number, beds: number): number {
  if (beds <= 0) return 0;
  return Math.round(noi / (beds * 12));
}


/**
 * Monthly mortgage payment: standard amortisation. Lifted out of
 * `client/pages/Financial.tsx` by CHAP-491, where it was a file-local helper,
 * so the capital-stack underwriting bar scores debt with the SAME arithmetic
 * the financial projection uses. Two implementations of an amortisation
 * formula on two pages is how two surfaces come to disagree about the same
 * loan.
 *
 * `annualRate` is a fraction (0.065), not a percentage.
 */
export function calculatePMT(annualRate: number, termYears: number, principal: number): number {
  if (annualRate === 0) return principal / (termYears * 12);
  const r = annualRate / 12;
  const n = termYears * 12;
  return (principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

/**
 * Annual mortgage constant: the annual debt service per $1 of principal.
 * Multiply by a loan amount to get annual P&I, which is what a DSCR test
 * divides NOI by.
 */
export function annualMortgageConstant(annualRate: number, termYears: number): number {
  return calculatePMT(annualRate, termYears, 1) * 12;
}
